import { useState, useCallback, useRef } from 'react';
import { instagramApi, InstagramEmbeddedSignupConfig, InstagramEmbeddedSignupResult } from '../services/instagramApi';

// Facebook SDK types are defined in @/types/window.d.ts

/**
 * State for the Instagram Embedded Signup flow
 */
interface EmbeddedSignupState {
  isLoading: boolean;
  isSDKLoaded: boolean;
  isSDKInitialized: boolean;
  error: string | null;
  result: InstagramEmbeddedSignupResult | null;
}

/**
 * Instagram account from API response
 */
interface InstagramAccount {
  id: string;
  username: string;
  name: string;
  pageId: string;
  pageName: string;
}

/**
 * Hook for Instagram Embedded Signup via Meta Platform
 *
 * Handles:
 * 1. Loading Facebook SDK dynamically
 * 2. Initializing SDK with config from backend
 * 3. Starting FB.login() with Instagram permissions
 * 4. Getting Instagram accounts linked to Facebook pages
 * 5. Completing signup flow with backend
 *
 * @example
 * ```tsx
 * const { startEmbeddedSignup, isLoading, error, result } = useInstagramEmbeddedSignup();
 *
 * if (result) {
 *   // Use result.accessToken, result.instagramAccountId, result.pageId
 * }
 * ```
 */
export function useInstagramEmbeddedSignup() {
  const [state, setState] = useState<EmbeddedSignupState>({
    isLoading: false,
    isSDKLoaded: false,
    isSDKInitialized: false,
    error: null,
    result: null,
  });

  const configRef = useRef<InstagramEmbeddedSignupConfig | null>(null);
  const pendingAccessTokenRef = useRef<string | null>(null);

  // Store accounts for selection flow
  const [availableAccounts, setAvailableAccounts] = useState<InstagramAccount[]>([]);
  const [accountSelectionRequired, setAccountSelectionRequired] = useState(false);

  /**
   * Load Facebook SDK script dynamically
   */
  const loadSDK = useCallback((): Promise<void> => {
    return new Promise((resolve, reject) => {
      // Check if already loaded
      if (window.FB) {
        setState(prev => ({ ...prev, isSDKLoaded: true }));
        resolve();
        return;
      }

      // Check if script already exists
      if (document.getElementById('facebook-jssdk')) {
        // SDK script exists but FB not available yet - wait for it
        const checkFB = setInterval(() => {
          if (window.FB) {
            clearInterval(checkFB);
            setState(prev => ({ ...prev, isSDKLoaded: true }));
            resolve();
          }
        }, 100);

        // Timeout after 10 seconds
        setTimeout(() => {
          clearInterval(checkFB);
          reject(new Error('Facebook SDK load timeout'));
        }, 10000);
        return;
      }

      // Load SDK script
      window.fbAsyncInit = () => {
        setState(prev => ({ ...prev, isSDKLoaded: true }));
        resolve();
      };

      const script = document.createElement('script');
      script.id = 'facebook-jssdk';
      script.src = 'https://connect.facebook.net/en_US/sdk.js';
      script.async = true;
      script.defer = true;
      script.crossOrigin = 'anonymous';

      script.onerror = () => {
        reject(new Error('Failed to load Facebook SDK'));
      };

      document.body.appendChild(script);
    });
  }, []);

  /**
   * Initialize Facebook SDK with config from backend
   */
  const initializeSDK = useCallback(async (): Promise<InstagramEmbeddedSignupConfig> => {
    if (!window.FB) {
      throw new Error('Facebook SDK not loaded');
    }

    // Get config from backend
    const config = await instagramApi.getEmbeddedSignupConfig();
    configRef.current = config;

    // Initialize FB SDK
    window.FB.init({
      appId: config.appId,
      cookie: true,
      xfbml: true,
      version: config.graphApiVersion,
    });

    setState(prev => ({ ...prev, isSDKInitialized: true }));
    console.log('[Instagram Embedded Signup] SDK initialized with config:', {
      appId: config.appId,
      version: config.graphApiVersion,
    });

    return config;
  }, []);

  /**
   * Complete the signup flow with selected Instagram account
   */
  const completeSignup = useCallback(async (
    accessToken: string,
    instagramAccountId: string,
    pageId: string
  ) => {
    try {
      console.log('[Instagram Embedded Signup] Completing signup with backend:', {
        instagramAccountId,
        pageId,
      });

      const result = await instagramApi.completeEmbeddedSignup({
        accessToken,
        instagramAccountId,
        pageId,
      });

      setState(prev => ({
        ...prev,
        isLoading: false,
        result,
        error: null,
      }));

      setAccountSelectionRequired(false);
      setAvailableAccounts([]);

      console.log('[Instagram Embedded Signup] Signup completed successfully:', {
        webhookConfigured: result.webhookConfigured,
      });
    } catch (error: any) {
      console.error('[Instagram Embedded Signup] Failed to complete signup:', error);
      setState(prev => ({
        ...prev,
        isLoading: false,
        error: error.response?.data?.message || error.message || 'Erro ao finalizar configuração',
      }));
    }
  }, []);

  /**
   * Handle Instagram account selection (when multiple accounts available)
   */
  const selectInstagramAccount = useCallback(async (account: InstagramAccount) => {
    if (!pendingAccessTokenRef.current) {
      setState(prev => ({
        ...prev,
        error: 'Token de acesso expirado. Por favor, tente novamente.',
      }));
      return;
    }

    setState(prev => ({ ...prev, isLoading: true }));
    await completeSignup(pendingAccessTokenRef.current, account.id, account.pageId);
  }, [completeSignup]);

  /**
   * Process FB.login response - handles async operations after receiving code
   * This is called separately from the FB.login callback to avoid async function type issues
   */
  const processLoginResponse = useCallback(async (accessToken: string) => {
    try {
      // Get Instagram accounts linked to user's pages
      console.log('[Instagram Embedded Signup] Fetching Instagram accounts with access token...');
      const accounts = await instagramApi.getInstagramAccounts(accessToken);

      console.log('[Instagram Embedded Signup] Found accounts:', accounts.length);

      if (accounts.length === 0) {
        setState(prev => ({
          ...prev,
          isLoading: false,
          error: 'Nenhuma conta Instagram Business encontrada. Certifique-se de ter uma conta Instagram Business vinculada a uma Página do Facebook.',
        }));
        return;
      }

      if (accounts.length === 1) {
        // Single account - complete signup automatically
        await completeSignup(accessToken, accounts[0].id, accounts[0].pageId);
      } else {
        // Multiple accounts - require selection
        setAvailableAccounts(accounts);
        setAccountSelectionRequired(true);
        setState(prev => ({ ...prev, isLoading: false }));
      }
    } catch (error: any) {
      console.error('[Instagram Embedded Signup] Failed to get accounts:', error);
      setState(prev => ({
        ...prev,
        isLoading: false,
        error: error.response?.data?.message || error.message || 'Erro ao buscar contas do Instagram',
      }));
    }
  }, [completeSignup]);

  /**
   * Start the Embedded Signup flow
   */
  const startEmbeddedSignup = useCallback(async () => {
    setState(prev => ({ ...prev, isLoading: true, error: null, result: null }));
    setAccountSelectionRequired(false);
    setAvailableAccounts([]);

    try {
      // Step 1: Load SDK if needed
      if (!state.isSDKLoaded) {
        await loadSDK();
      }

      // Step 2: Initialize SDK with config from backend
      let config = configRef.current;
      if (!state.isSDKInitialized || !config) {
        config = await initializeSDK();
      }

      // Step 3: Start FB.login() with Instagram permissions
      // NOTE: FB.login callback MUST be a regular function, not async
      console.log('[Instagram Embedded Signup] Starting FB.login() with config_id:', config.configId);

      if (!window.FB) {
        throw new Error('Facebook SDK not available');
      }

      window.FB.login(
        (response: FBLoginResponse) => {
          console.log('[Instagram Embedded Signup] FB.login response:', {
            status: response.status,
            hasAccessToken: !!response.authResponse?.accessToken,
          });

          if (response.authResponse?.accessToken) {
            const accessToken = response.authResponse.accessToken;
            pendingAccessTokenRef.current = accessToken;

            // Call async processing function separately
            // This avoids the "asyncfunction" type error from FB.login
            processLoginResponse(accessToken);
          } else {
            // User cancelled or error
            setState(prev => ({
              ...prev,
              isLoading: false,
              error: response.status === 'not_authorized'
                ? 'Autorização negada pelo usuário'
                : 'Não foi possível obter autorização do Facebook',
            }));
          }
        },
        {
          config_id: config.configId,
          extras: {
            sessionInfoVersion: 2,
          },
        }
      );
    } catch (error: any) {
      console.error('[Instagram Embedded Signup] Error:', error);
      setState(prev => ({
        ...prev,
        isLoading: false,
        error: error.message || 'Erro ao iniciar processo de login',
      }));
    }
  }, [state.isSDKLoaded, state.isSDKInitialized, loadSDK, initializeSDK, processLoginResponse]);

  /**
   * Reset state to initial values
   */
  const reset = useCallback(() => {
    setState({
      isLoading: false,
      isSDKLoaded: state.isSDKLoaded, // Keep SDK loaded state
      isSDKInitialized: state.isSDKInitialized, // Keep SDK initialized state
      error: null,
      result: null,
    });
    pendingAccessTokenRef.current = null;
    setAvailableAccounts([]);
    setAccountSelectionRequired(false);
  }, [state.isSDKLoaded, state.isSDKInitialized]);

  return {
    // State
    state,
    isReady: !state.isLoading && !state.error,
    isLoading: state.isLoading,
    error: state.error,
    result: state.result,

    // Actions
    startEmbeddedSignup,
    reset,

    // SDK status
    isSDKLoaded: state.isSDKLoaded,
    isSDKInitialized: state.isSDKInitialized,

    // Account selection (for multiple accounts)
    accountSelectionRequired,
    availableAccounts,
    selectInstagramAccount,
  };
}
